import { z } from 'zod';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { CadRendererGridAppearanceSchema, createDefaultGridAppearance } from './bridges/grid-bridge.js';

import type { ModelType } from '@cadstrom/sigma-engine-shared';
import type { CadRendererGridAppearance } from './bridges/grid-bridge.js';

const GRID_APPEARANCE_STORAGE_KEY = 'cad-renderer-grid-appearance';

export interface GridAppearanceStore {
	/** The chosen grid appearance per model type. A missing entry means nothing has been chosen yet. Persisted. */
	appearances: Partial<Record<ModelType, CadRendererGridAppearance>>;

	/**
	 * Merges appearance changes into the preference for one model type.
	 *
	 * @param modelType - Model type whose preference changes.
	 * @param appearance - Fields to change; the rest keep their current or default values.
	 */
	setAppearance: (modelType: ModelType, appearance: Partial<CadRendererGridAppearance>) => void;
}

const SavedAppearanceSchema = CadRendererGridAppearanceSchema.optional().catch(undefined);

const PersistedGridAppearancesSchema = z.object({
	appearances: z
		.object({ schematic: SavedAppearanceSchema, pcb: SavedAppearanceSchema })
		.catch({})
		.transform(({ schematic, pcb }) => {
			const appearances: GridAppearanceStore['appearances'] = {};
			if (schematic) {
				appearances.schematic = schematic;
			}
			if (pcb) {
				appearances.pcb = pcb;
			}
			return appearances;
		}),
});

const StoredGridAppearancesSchema = z.object({ state: PersistedGridAppearancesSchema });

/**
 * Reads the preference as most recently saved by any window of this origin.
 *
 * @returns The valid saved entries per model type, or an empty map when nothing valid is saved.
 */
function readSavedAppearances(): GridAppearanceStore['appearances'] {
	try {
		const raw = localStorage.getItem(GRID_APPEARANCE_STORAGE_KEY);
		const result = StoredGridAppearancesSchema.safeParse(raw === null ? null : JSON.parse(raw));
		return result.success ? result.data.state.appearances : {};
	} catch {
		return {};
	}
}

/**
 * The user's grid appearance preference, one per model type, kept in this browser across reloads.
 * Each model type's saved entry is validated on its own; an invalid one falls back to the current
 * value without affecting the other. Changes start from what any window last saved, so windows
 * editing different model types don't overwrite each other.
 */
export const useGridAppearanceStore = create<GridAppearanceStore>()(
	persist(
		(set) => ({
			appearances: {},

			setAppearance: (modelType, appearance) =>
				set((state) => {
					const appearances = { ...state.appearances, ...readSavedAppearances() };
					return {
						appearances: {
							...appearances,
							[modelType]: { ...(appearances[modelType] ?? createDefaultGridAppearance()), ...appearance },
						},
					};
				}),
		}),
		{
			name: GRID_APPEARANCE_STORAGE_KEY,
			partialize: (state) => ({ appearances: state.appearances }),
			merge: (persisted, current) => {
				const result = PersistedGridAppearancesSchema.safeParse(persisted);
				return result.success ?
						{ ...current, appearances: { ...current.appearances, ...result.data.appearances } }
					:	current;
			},
		},
	),
);

/**
 * Reloads the preference whenever another window of this origin saves it, so every open window
 * shows the same grid.
 *
 * @returns A callback that stops listening.
 */
export function followGridAppearanceStorage(): () => void {
	const handleStorage = (event: StorageEvent) => {
		if (event.key === GRID_APPEARANCE_STORAGE_KEY) {
			void useGridAppearanceStore.persist.rehydrate();
		}
	};
	window.addEventListener('storage', handleStorage);
	return () => window.removeEventListener('storage', handleStorage);
}
